import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CommentOutlined, DownOutlined, EditOutlined, MessageOutlined, SettingOutlined, SyncOutlined } from '@ant-design/icons';
import { App, Button, Col, Dropdown, Row, Space, Spin } from 'antd';

import { SmsServiceType } from '../../../shared/constants/enums';
import GlobalTelecomAccount from '../modals/global-telecom-account-modal';
import MassSMS from '../modals/mass-sms';
import RingCentralAccountSettingsModal from '../modals/ring-central-account-settings-modal';
import SmsMigrate from '../modals/sms-migrate-modal';
import SmsSetting from '../modals/sms-setting-modal';
import SpokeAccountEdit from '../modals/spoke-account-edit-modal';
import TwilioSettings from '../modals/twilio-settings-modal';
import * as actions from '../store/actions';

import { AppDispatch, RootState } from '../../../store';

import './styles.less';

export type SmsMessagesHeaderProps = {
  updateMessageControl: () => void;
};

const SmsMessagesHeader = ({ updateMessageControl }: SmsMessagesHeaderProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const { message } = App.useApp();

  const [showSpokeAccountModal, setShowSpokeAccountModal] = useState<boolean>(false);
  const [showGlobalTelecomAccountModal, setShowGlobalTelecomAccountModal] = useState<boolean>(false);
  const [showTwilioAccountModal, setShowTwilioAccountModal] = useState<boolean>(false);
  const [showRingCentralAccountSettingsModal, setShowRingCentralAccountSettingsModal] = useState<boolean>(false);
  const [showSmsSettingModal, setShowSmsSettingModal] = useState<boolean>(false);
  const [showSmsMigrateModal, setShowSmsMigrateModal] = useState<boolean>(false);
  const [showMassSms, setShowMassSms] = useState<boolean>(false);
  const [loadingIndicator, setLoadingIndicator] = useState<() => void>();

  const { messageControlParameters, messageControlParametersLoading, messagesLoading, sendMessageResultLoading } = useSelector(
    (state: RootState) => state.smses,
  );
  const { chatsLoading, updateChatNamesResultLoading } = useSelector((state: RootState) => state.smsChat);

  useEffect(() => {
    if (messageControlParameters && messageControlParameters.smsMigrateIsCompleted === null) {
      setShowSmsMigrateModal(true);
    }
  }, [messageControlParameters?.smsMigrateIsCompleted]);

  useEffect(() => {
    showSpokeAccountModal ||
    showTwilioAccountModal ||
    showRingCentralAccountSettingsModal ||
    showSmsMigrateModal ||
    showSmsSettingModal ||
    showGlobalTelecomAccountModal ||
    showMassSms
      ? (document.body.style.overflow = 'hidden')
      : (document.body.style.overflow = 'unset');
  }, [
    showSpokeAccountModal,
    showTwilioAccountModal,
    showRingCentralAccountSettingsModal,
    showSmsMigrateModal,
    showSmsSettingModal,
    showGlobalTelecomAccountModal,
    showMassSms,
  ]);

  useEffect(() => {
    if (updateChatNamesResultLoading) {
      const hide = message.loading('Action in progress..', 0);
      setLoadingIndicator(() => hide);
    } else if (loadingIndicator) {
      loadingIndicator();
    }
  }, [updateChatNamesResultLoading]);

  const onCloseSpokeAccountSettingsModal = () => setShowSpokeAccountModal(false);

  const getTitle = () => {
    if (!messageControlParameters || messageControlParameters.smsServiceType === SmsServiceType.None) {
      return 'No SMS service selected';
    }

    switch (messageControlParameters.smsServiceType) {
      case SmsServiceType.DefaultRingCentral:
        return 'Default (1-way SMS)';

      case SmsServiceType.OwnRingCentral:
        return messageControlParameters.myNumber + ' Own RingCentral (2-way SMS/MMS; Mass messages)';

      case SmsServiceType.AgencyOwnRingCentral:
        return messageControlParameters.myNumber + ' Agency Own RingCentral (2-way SMS/MMS; Mass messages)';

      case SmsServiceType.DedicatedRingCentral:
        return messageControlParameters.myNumber + ' Dedicated RingCentral (2-way SMS/MMS; Mass messages)';

      case SmsServiceType.Twilio:
        return messageControlParameters.myNumber + ' Twilio (2-way SMS/MMS; Mass messages)';

      case SmsServiceType.AgencyTwilio:
        return messageControlParameters.myNumber + ' Agency Twilio (2-way SMS/MMS; Mass messages)';

      case SmsServiceType.Spoke:
        return messageControlParameters.myNumber + ' Spoke (2-way SMS/MMS; Mass messages)';

      case SmsServiceType.AgencySpoke:
        return messageControlParameters.myNumber + ' Agency Spoke (2-way SMS/MMS; Mass messages)';

      case SmsServiceType.GlobalTelecom:
        return messageControlParameters.myNumber + ' Global Telecom (2-way SMS/MMS; Mass messages)';

      case SmsServiceType.AgencyGlobalTelecom:
        return messageControlParameters.myNumber + ' Agency Global Telecom (2-way SMS/MMS; Mass messages)';

      case SmsServiceType.PhoneGroup:
        return messageControlParameters.myNumber + ' Phone Group (2-way SMS/MMS; Mass messages)';
    }
  };

  const onCloseTwilioAccountSettingsModal = () => {
    setShowTwilioAccountModal(false);
  };

  const onCloseRingCentralAccountSettingsModal = () => {
    setShowRingCentralAccountSettingsModal(false);
  };

  const onCloseSmsSettingModal = () => {
    setShowSmsSettingModal(false);
  };

  const onCloseGlobalTelecomAccountSettingsModal = () => {
    setShowGlobalTelecomAccountModal(false);
  };

  const onCloseSmsMergeModal = () => {
    setShowSmsMigrateModal(false);
  };

  const resetAndRefresh = () => {
    dispatch(actions.setSelectedChatItem(undefined));
    updateMessageControl?.();
    refresh();
  };

  const refresh = () => {
    dispatch(actions.getUpdatesSuccess({ chat: true, message: true }));
  };

  const updateChatNames = () => {
    if (!messageControlParameters) {
      return;
    }

    dispatch(
      actions.updateChatNames({
        ownerAccountUserId: messageControlParameters.ownerAccountUserId,
        smsServiceType: messageControlParameters.smsServiceType,
        myPhoneNumber: messageControlParameters.myNumber,
      }),
    ).then(resetAndRefresh);
  };

  const onCloseMassSmsModal = () => {
    setShowMassSms(false);
  };

  return (
    <Row>
      <Col span={15}>
        <Space>
          <div>SMS service: {getTitle()}</div>
        </Space>
      </Col>
      <Col span={9} className="twilio-control-buttons">
        <Spin spinning={messageControlParametersLoading}>
          <Space>
            <Button
              onClick={refresh}
              loading={sendMessageResultLoading === true || messagesLoading === true || chatsLoading === true}
              disabled={sendMessageResultLoading === true || messagesLoading === true || chatsLoading === true}
              icon={<SyncOutlined />}
              type="text"
            />
            <Dropdown
              menu={{
                items: [
                  {
                    label: (
                      <>
                        <MessageOutlined /> Twilio Settings
                      </>
                    ),
                    key: 'Twilio Settings',
                    onClick: () => setShowTwilioAccountModal(true),
                  },
                  {
                    label: (
                      <>
                        <MessageOutlined /> Spoke Settings
                      </>
                    ),
                    key: 'Spoke Settings',
                    onClick: () => setShowSpokeAccountModal(true),
                  },
                  {
                    label: (
                      <>
                        <MessageOutlined /> Global Telecom Settings
                      </>
                    ),
                    key: 'Global Telecom Settings',
                    onClick: () => setShowGlobalTelecomAccountModal(true),
                  },
                  {
                    label: (
                      <>
                        <MessageOutlined /> Ring Central Settings
                      </>
                    ),
                    key: 'Ring Central Settings',
                    onClick: () => setShowRingCentralAccountSettingsModal(true),
                  },
                  // {
                  //   label: (
                  //     <>
                  //       <LoginOutlined /> Migrate old SMSes to the new
                  //     </>
                  //   ),
                  //   key: 'Migrate old SMSes to the new',
                  //   onClick: () => setShowSmsMigrateModal(true),
                  // },
                  {
                    label: (
                      <>
                        <EditOutlined /> Update chat names
                      </>
                    ),
                    key: 'Update chat names',
                    onClick: updateChatNames,
                  },
                  {
                    label: (
                      <>
                        <CommentOutlined /> Mass SMS
                      </>
                    ),
                    key: 'Mass SMS',
                    onClick: () => setShowMassSms(true),
                  },
                  {
                    label: (
                      <>
                        <SettingOutlined /> System Settings
                      </>
                    ),
                    key: 'System Settings',
                    onClick: () => setShowSmsSettingModal(true),
                  },
                ],
              }}
              trigger={['click']}
            >
              <Button type="primary" ghost size="small">
                Actions <DownOutlined />
              </Button>
            </Dropdown>
          </Space>
        </Spin>
      </Col>

      {showSpokeAccountModal ? (
        <SpokeAccountEdit showModal={showSpokeAccountModal} onCloseModal={onCloseSpokeAccountSettingsModal} resetAndRefresh={resetAndRefresh} />
      ) : null}

      {showTwilioAccountModal ? (
        <TwilioSettings showModal={showTwilioAccountModal} onCloseModal={onCloseTwilioAccountSettingsModal} resetAndRefresh={resetAndRefresh} />
      ) : null}

      {showRingCentralAccountSettingsModal ? (
        <RingCentralAccountSettingsModal
          showModal={showRingCentralAccountSettingsModal}
          onCloseModal={onCloseRingCentralAccountSettingsModal}
          resetAndRefresh={resetAndRefresh}
        />
      ) : null}

      {showSmsSettingModal ? (
        <SmsSetting showModal={showSmsSettingModal} onCloseModal={onCloseSmsSettingModal} resetAndRefresh={resetAndRefresh} />
      ) : null}

      {showSmsMigrateModal ? (
        <SmsMigrate showModal={showSmsMigrateModal} onCloseModal={onCloseSmsMergeModal} resetAndRefresh={resetAndRefresh} />
      ) : null}

      {showGlobalTelecomAccountModal ? (
        <GlobalTelecomAccount
          showModal={showGlobalTelecomAccountModal}
          onCloseModal={onCloseGlobalTelecomAccountSettingsModal}
          resetAndRefresh={resetAndRefresh}
        />
      ) : null}

      {showMassSms ? <MassSMS showModal={showMassSms} onClose={onCloseMassSmsModal} /> : null}
    </Row>
  );
};

export default SmsMessagesHeader;
